import { Controls } from '../systems/Controls';

/**
 * Keyboard input. Primary flight axes are set as *targets* while a key is held
 * (self-centering on release); throttle/mixture/trim/flaps are incremental.
 */
export class Keyboard {
  private held = new Set<string>();
  onReset?: () => void;
  onHardReset?: () => void;
  onVisibility?: (preset: number) => void;
  onTogglePause?: () => void;

  constructor(private controls: Controls) {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    // Let modifier-combos (e.g. Ctrl+F fullscreen, Ctrl+J joystick config) pass
    // through to the browser/app-level handlers instead of also triggering a
    // sim command.
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const k = e.key.toLowerCase();
    this.held.add(k);
    // One-shot actions
    switch (k) {
      case 'pageup': this.controls.flapsUp(); break;
      case 'pagedown': this.controls.flapsDown(); break;
      case 'g': this.controls.toggleGear(); break;
      case 'b': this.controls.parkingBrake = !this.controls.parkingBrake; break;
      case 'r': e.shiftKey ? this.onHardReset?.() : this.onReset?.(); break;
      case 'p': this.onTogglePause?.(); break;
      case '1': this.onVisibility?.(0); break;
      case '2': this.onVisibility?.(1); break;
      case '3': this.onVisibility?.(2); break;
    }
    if (['arrowup', 'arrowdown', 'arrowleft', 'arrowright', 'pageup', 'pagedown', ' '].includes(k)) {
      e.preventDefault();
    }
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    this.held.delete(e.key.toLowerCase());
  };

  /** Called each sim step to translate held keys into control commands. */
  poll(dt: number): void {
    const c = this.controls;

    // Elevator: W nose-down stick-forward, S nose-up (aft). Positive = nose up.
    // Keyboard keys are on/off, so scale their authority below full deflection
    // for smoother handling (a joystick still commands the full range directly).
    const PITCH_AUTH = 0.55, ROLL_AUTH = 0.7, YAW_AUTH = 0.7;

    let elev = 0;
    if (this.held.has('s')) elev += PITCH_AUTH;
    if (this.held.has('w')) elev -= PITCH_AUTH;
    c.elevatorTarget = elev;

    // Aileron: D right, A left.
    let ail = 0;
    if (this.held.has('d')) ail += ROLL_AUTH;
    if (this.held.has('a')) ail -= ROLL_AUTH;
    c.aileronTarget = ail;

    // Rudder: '.' right, ',' left.
    let rud = 0;
    if (this.held.has('.')) rud += YAW_AUTH;
    if (this.held.has(',')) rud -= YAW_AUTH;
    c.rudderTarget = rud;

    // Throttle (engine power): '+' / '-' (main row and numpad both report the
    // same e.key), plus '[' / ']' and Insert/Delete as long-standing aliases.
    const thrRate = 0.6 * dt;
    if (this.held.has('+') || this.held.has('=') || this.held.has(']') || this.held.has('insert')) {
      c.adjustThrottle(thrRate);
    }
    if (this.held.has('-') || this.held.has('_') || this.held.has('[') || this.held.has('delete')) {
      c.adjustThrottle(-thrRate);
    }

    // Mixture: Home rich, End lean.
    const mixRate = 0.5 * dt;
    if (this.held.has('home')) c.adjustMixture(mixRate);
    if (this.held.has('end')) c.adjustMixture(-mixRate);

    // Trim: Arrow Up nose-up, Arrow Down nose-down (T/Y kept as aliases).
    // (Reported reversed in testing — flipped the key-to-sign mapping here.)
    const trimRate = 0.4 * dt;
    if (this.held.has('arrowup') || this.held.has('t')) c.adjustTrim(-trimRate);
    if (this.held.has('arrowdown') || this.held.has('y')) c.adjustTrim(trimRate);
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
  }
}
